import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/Toast';
import { getInsightsByParameters } from '../../../services/statistics/insights';
import { DimensionID } from '../../../enums/dimension';
import { searchGroups } from '../../../services/group/group';
import { UserRole } from '../../../enums/user';
import { shouldBypassStatisticsCacheForSuperAdmin } from '../../../services/statistics/standardStatisticsCacheEligibility';
import { representationOptionsValues } from '../../../services/utils/representationOptions';
import { RangeParams } from '@viniarruda/react-month-range-picker/types/MonthRangePicker/types';
import { useCompany } from '../../../context/CompanyContext';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../i18n/useAppLocale';
import {
    GroupParticipationPeriodEntry,
    GroupParticipationReportRow,
    InsightGroupOption,
    InsightsMonthData,
} from '../../../interfaces/insights.interface';

// Data Models
export type PageDataModel = {
    ipaHistoric: any[], iepHistoric: any[], ifczHistoric: any[], ifozHistoric: any[],
    epHistoric: any[], engHistoric: any[], relHistoric: any[], sigHistoric: any[], reaHistoric: any[], vitHistoric: any[],
    filteredIfozHistoric: any[], filteredIapHistoric: any[], filteredIepHistoric: any[], filteredIfczHistoric: any[],
    filteredEpHistoric: any[], filteredEngHistoric: any[], filteredRelHistoric: any[], filteredSigHistoric: any[],
    filteredReaHistoric: any[], filteredVitHistoric: any[],
    points: any,
    pointVit: number | null, pointRel: number | null, pointReal: number | null, pointSig: number | null,
    pointEng: number | null, pointEp: number | null,
    filteredPointEp: number | null, filteredPointEng: number | null, filteredPointRel: number | null,
    filteredPointSig: number | null, filteredPointReal: number | null, filteredPointVit: number | null
};

type LoadContext = {
    representationSelectedOptions?: string[];
    selectedGroupOptions?: string[];
    availableGroupOptions?: InsightGroupOption[];
};

const monthNamesPt = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro'
];

const normalizeText = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const getMonthIndexFromName = (monthName: string) => {
    const normalizedMonth = normalizeText(monthName);
    const monthIndex = monthNamesPt.findIndex((name) => normalizeText(name) === normalizedMonth);
    return monthIndex >= 0 ? monthIndex : 0;
};

const buildMonthKey = (year: number, monthIndex: number) => `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

const buildPeriodSequence = (endYear: number, endMonth: number, monthsRange: number) => {
    const safeMonthsRange = Math.max(monthsRange || 1, 1);
    const endDate = new Date(endYear, endMonth - 1, 1);
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - safeMonthsRange + 1);

    const sequence: Array<{ date: Date; key: string; month: string }> = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        sequence.push({
            date: new Date(currentDate),
            key: buildMonthKey(currentDate.getFullYear(), currentDate.getMonth()),
            month: `${monthNamesPt[currentDate.getMonth()]}/${currentDate.getFullYear()}`
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return sequence;
};

const toNumberOrNull = (value: unknown) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null;
    }

    return value;
};

const buildPeriodEntries = (
    monthlyResults: InsightsMonthData[],
    periodSequence: Array<{ date: Date; key: string; month: string }>,
    getValueFromMonth: (monthResult: InsightsMonthData) => number | null | undefined
): GroupParticipationPeriodEntry[] => {
    const valuesByMonth = new Map<string, number | null>();

    monthlyResults.forEach((monthResult) => {
        const monthIndex = getMonthIndexFromName(monthResult.month);
        const monthKey = buildMonthKey(monthResult.year, monthIndex);
        valuesByMonth.set(monthKey, toNumberOrNull(getValueFromMonth(monthResult)));
    });

    return periodSequence.map(({ date, key, month }) => ({
        date,
        month,
        value: valuesByMonth.has(key) ? valuesByMonth.get(key) ?? null : null
    }));
};

const calculateAverage = (values: Array<number | null>) => {
    const numericValues = values.filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

    if (numericValues.length === 0) {
        return null;
    }

    const total = numericValues.reduce((sum, value) => sum + value, 0);
    return total / numericValues.length;
};

const calculateVariation = (values: Array<number | null>) => {
    const numericValues = values.filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

    if (numericValues.length < 2) {
        return null;
    }

    return numericValues[numericValues.length - 1] - numericValues[0];
};

const buildReportRow = (
    groupId: string,
    groupName: string,
    monthlyResults: InsightsMonthData[],
    periodSequence: Array<{ date: Date; key: string; month: string }>,
    getValueFromMonth: (monthResult: InsightsMonthData) => number | null | undefined
): GroupParticipationReportRow => {
    const periodAdherence = buildPeriodEntries(monthlyResults, periodSequence, getValueFromMonth);
    const values = periodAdherence.map((entry) => entry.value);

    return {
        groupId,
        groupName,
        periodAdherence,
        lastMonthAdherence: values.length > 0 ? values[values.length - 1] : null,
        average: calculateAverage(values),
        variation: calculateVariation(values)
    };
};

const getSelectedSexIds = (representationFilters: string[] = []) => representationFilters
    .map((key) => representationOptionsValues[key])
    .filter((value): value is number => typeof value === 'number');

const getSelectedAgeRanges = (representationFilters: string[] = []) => representationFilters
    .map((key) => representationOptionsValues[key])
    .filter((value): value is number[] => Array.isArray(value));

const getActiveOrPermittedGroupIds = (availableGroupOptions: InsightGroupOption[] = []) => availableGroupOptions.map((group) => group.value);

const getMonthMetricValue = (
    monthResult: InsightsMonthData,
    scope: 'generalData' | 'filteredDataCombined',
    metric: 'ifoz' | 'iap' | 'iep' | 'ifcz'
) => toNumberOrNull(monthResult?.[scope]?.[metric]);

const getMonthDimensionValue = (
    monthResult: InsightsMonthData,
    scope: 'generalData' | 'filteredDataCombined',
    dimensionId: DimensionID
) => toNumberOrNull(monthResult?.[scope]?.dimensionsResults?.[dimensionId]?.dimensionTotal?.ifoz);

/**
 * Hook to manage data and business logic for the Insights page.
 * It encapsulates state management, data fetching, and filtering logic.
 */
export const useInsightsData = () => {
    const { t } = useTranslation('insights');
    const appLocale = useAppLocale();
    const { user, SignOut } = useAuth();
    const shouldBypassStatisticsCache = shouldBypassStatisticsCacheForSuperAdmin(user.user?.role);
    const { showToast } = useToast();
    const { companies: allCompanies, loading: loadingCompanies } = useCompany();
    const companyCreatedAt = user.company_visited?.created_at ?? null;
    const [loading, setLoading] = useState(true);
    const [loadingIndicators, setLoadingIndicators] = useState(true);
    const [loadingEpHistoric, setLoadingEpHistoric] = useState(true);
    const [loadingEngHistoric, setLoadingEngHistoric] = useState(true);
    const [loadingRelHistoric, setLoadingRelHistoric] = useState(true);
    const [loadingSigHistoric, setLoadingSigHistoric] = useState(true);
    const [loadingReaHistoric, setLoadingReaHistoric] = useState(true);
    const [loadingVitHistoric, setLoadingVitHistoric] = useState(true);
    const [loadingGroupParticipationReport, setLoadingGroupParticipationReport] = useState(false);

    const [surveysAllMonth, setSurveysAllMonth] = useState<boolean>(true);

    const [pageData, setPageData] = useState<PageDataModel>({
        ipaHistoric: [], iepHistoric: [], ifczHistoric: [], ifozHistoric: [], epHistoric: [], engHistoric: [],
        relHistoric: [], sigHistoric: [], reaHistoric: [], vitHistoric: [], filteredIfozHistoric: [],
        filteredIapHistoric: [], filteredIepHistoric: [], filteredIfczHistoric: [], filteredEpHistoric: [],
        filteredEngHistoric: [], filteredRelHistoric: [], filteredSigHistoric: [], filteredReaHistoric: [],
        filteredVitHistoric: [], points: null, pointVit: 0, pointRel: 0, pointReal: 0, pointSig: 0, pointEng: 0,
        pointEp: 0, filteredPointEp: null, filteredPointEng: null, filteredPointRel: null, filteredPointSig: null,
        filteredPointReal: null, filteredPointVit: null,
    });

    const [groupParticipationReportRows, setGroupParticipationReportRows] = useState<GroupParticipationReportRow[]>([]);

    const [dateValue, setDateValue] = useState<string>('');
    const [range, setRange] = useState<RangeParams>({ startMonth: 1, startYear: 2025, endMonth: 10, endYear: 2025 });
    const [selectGroupOptions, setSelectGroupOptions] = useState<InsightGroupOption[]>([]);
    const [representationSelectedOptions, setRepresentationSelectedOptions] = useState<string[]>([]);
    const [selectedGroupOptions, setSelectedGroups] = useState<string[]>([]);
    const [appliedRepresentationSelectedOptions, setAppliedRepresentationSelectedOptions] = useState<string[]>([]);
    const [appliedGroupOptions, setAppliedGroupOptions] = useState<string[]>([]);

    const didMountRef = useRef(false);
    const reportRequestIdRef = useRef(0);
    const dataRequestIdRef = useRef(0);

    const clearFilteredData = useCallback(() => {
        setPageData(prevState => ({
            ...prevState,
            filteredIfozHistoric: [], filteredIapHistoric: [], filteredIepHistoric: [], filteredIfczHistoric: [],
            filteredEpHistoric: [], filteredEngHistoric: [], filteredRelHistoric: [], filteredSigHistoric: [],
            filteredReaHistoric: [], filteredVitHistoric: [], filteredPointEp: null, filteredPointEng: null,
            filteredPointRel: null, filteredPointSig: null, filteredPointReal: null, filteredPointVit: null,
        }));
    }, []);

    const loadGroupParticipationReportData = useCallback(async ({
        year,
        month,
        monthsRange,
        representationSelectedOptions: representationFilters = [],
        selectedGroupOptions: selectedGroups = [],
        availableGroupOptions = [],
        useStandardCache = false
    }: {
        year: string;
        month: string;
        monthsRange: number;
        representationSelectedOptions?: string[];
        selectedGroupOptions?: string[];
        availableGroupOptions?: InsightGroupOption[];
        useStandardCache?: boolean;
    }) => {
        const company = user.company_visited?.id;
        const currentRequestId = ++reportRequestIdRef.current;

        if (!company) {
            if (currentRequestId === reportRequestIdRef.current) {
                setGroupParticipationReportRows([]);
                setLoadingGroupParticipationReport(false);
            }
            return;
        }

        const groupIdsToUse = selectedGroups.length > 0 ? selectedGroups : getActiveOrPermittedGroupIds(availableGroupOptions);

        if (groupIdsToUse.length === 0) {
            if (currentRequestId === reportRequestIdRef.current) {
                setGroupParticipationReportRows([]);
                setLoadingGroupParticipationReport(false);
            }
            return;
        }

        const selectedSexes = getSelectedSexIds(representationFilters);
        const selectedAgeRanges = getSelectedAgeRanges(representationFilters);

        const groupNameMap = new Map(availableGroupOptions.map((group) => [group.value, group.label]));
        const orderedGroupIds = [...groupIdsToUse].sort((left, right) => {
            const leftLabel = groupNameMap.get(left) ?? left;
            const rightLabel = groupNameMap.get(right) ?? right;
            return leftLabel.localeCompare(rightLabel, appLocale, { sensitivity: 'base', numeric: true });
        });
        const periodSequence = buildPeriodSequence(Number(year), Number(month), monthsRange);

        setLoadingGroupParticipationReport(true);
        setGroupParticipationReportRows([]);

        try {
            const monthlyResults = await getInsightsByParameters(company, month, year, monthsRange, {
                sexes: selectedSexes,
                ageRanges: selectedAgeRanges,
                groups: orderedGroupIds,
                separateDataByEachFilter: true,
                separateDataContextMode: 'contextual',
                responseScope: {
                    generalData: false,
                    filteredDataCombined: false,
                    filteredData: {
                        groups: {
                            metrics: ['iap']
                        }
                    }
                }
            }, useStandardCache && !shouldBypassStatisticsCache ? {
                enabled: true,
                userId: user.user?.id,
                scope: 'insights:group-participation'
            } : undefined);

            if (currentRequestId !== reportRequestIdRef.current) {
                return;
            }

            const reportRows = orderedGroupIds.map((groupId) => buildReportRow(
                groupId,
                groupNameMap.get(groupId) ?? groupId,
                monthlyResults,
                periodSequence,
                (monthResult) => monthResult.filteredData?.groups?.[groupId]?.iap
            ));

            setGroupParticipationReportRows(reportRows);
        } catch (error) {
            if (currentRequestId === reportRequestIdRef.current) {
                setGroupParticipationReportRows([]);
                showToast(t('error'), 'error')
            }
        } finally {
            if (currentRequestId === reportRequestIdRef.current) {
                setLoadingGroupParticipationReport(false);
            }
        }
    }, [showToast, user.company_visited?.id]);

    const loadFilteredData = useCallback(async (
        year?: string,
        month?: string,
        monthsRange?: number,
        overrides?: LoadContext
    ) => {
        const requestId = ++dataRequestIdRef.current;
        const representationFilters = overrides?.representationSelectedOptions ?? representationSelectedOptions;
        const selectedGroups = overrides?.selectedGroupOptions ?? selectedGroupOptions;
        const availableGroupOptions = overrides?.availableGroupOptions ?? selectGroupOptions;

        const thereIsSomeFilter = (representationFilters?.length > 0) || (selectedGroups?.length > 0);
        const monthsRangeToUse = monthsRange || (((range.endYear - range.startYear) * 12) + (range.endMonth - range.startMonth) + 1);
        const yearToUse = year || new Date().getFullYear().toString();
        const monthToUse = month || (new Date().getMonth() + 1).toString();

        if (!thereIsSomeFilter) {
            await loadData(yearToUse, monthToUse, monthsRangeToUse, availableGroupOptions, requestId);
            if (requestId === dataRequestIdRef.current) clearFilteredData();
            return;
        }

        setLoading(true);
        setLoadingIndicators(true);
        setLoadingEpHistoric(true);
        setLoadingEngHistoric(true);
        setLoadingRelHistoric(true);
        setLoadingSigHistoric(true);
        setLoadingReaHistoric(true);
        setLoadingVitHistoric(true);

        try {
            const company = user.company_visited?.id;
            if (!company) throw new Error('company_not_found');
            const companyCreatedAt = user.company_visited?.created_at ?? null;

            const selectedSexes = getSelectedSexIds(representationFilters);
            const selectedAgeRanges = getSelectedAgeRanges(representationFilters);

            const insightsFilteredResults = await getInsightsByParameters(company, monthToUse, yearToUse, monthsRangeToUse, {
                sexes: selectedSexes,
                ageRanges: selectedAgeRanges,
                groups: selectedGroups,
                separateDataByEachFilter: false,
                responseScope: {
                    generalData: {
                        metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions']
                    },
                    filteredDataCombined: {
                        metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions']
                    }
                }
            });

            if (requestId !== dataRequestIdRef.current) return;

            const filteredCompanyIFOZHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'filteredDataCombined', 'ifoz') }));
            const generalCompanyIFOZHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'ifoz') }));
            const companyIAPHistoric = insightsFilteredResults.map((monthResult) => ({
                month: monthResult.month,
                year: monthResult.year,
                result: getMonthMetricValue(monthResult, 'filteredDataCombined', 'iap')
            }));
            const generalCompanyIAPHistoric = insightsFilteredResults.map((monthResult) => ({
                month: monthResult.month,
                year: monthResult.year,
                result: getMonthMetricValue(monthResult, 'generalData', 'iap')
            }));
            const companyIEPHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'filteredDataCombined', 'iep') }));
            const generalCompanyIEPHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'iep') }));
            const companyIFCZHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'filteredDataCombined', 'ifcz') }));
            const generalCompanyIFCZHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'ifcz') }));

            const companyEpHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.POSITIVE_EMOTIONS) }));

            const generalCompanyEpHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.POSITIVE_EMOTIONS) }));

            const companyEngHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.ENGAGEMENT) }));

            const generalCompanyEngHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.ENGAGEMENT) }));

            const companyRelHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.RELATIONSHIP) }));

            const generalCompanyRelHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.RELATIONSHIP) }));

            const companySigHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.MEANING) }));

            const generalCompanySigHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.MEANING) }));

            const companyReaHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.ACCOMPLISHMENT) }));

            const generalCompanyReaHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.ACCOMPLISHMENT) }));

            const companyVitHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'filteredDataCombined', DimensionID.VITALITY) }));

            const generalCompanyVitHistoric = insightsFilteredResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.VITALITY) }));

            const recentInsight = insightsFilteredResults.find((item) => item.filteredDataCombined?.dimensionsResults);
            const oldestInsight = insightsFilteredResults.slice().reverse().find((item) => item.filteredDataCombined?.dimensionsResults);
            let filteredPointsEpDiff: number = 0, filteredPointEngDiff: number = 0, filteredPointsRelDiff: number = 0, filteredPointsSigDiff: number = 0, filteredPointsRealDiff: number = 0, filteredPointsVitDiff: number = 0;

            if (recentInsight && oldestInsight) {
                filteredPointsEpDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.POSITIVE_EMOTIONS) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.POSITIVE_EMOTIONS) ?? 0);
                filteredPointEngDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.ENGAGEMENT) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.ENGAGEMENT) ?? 0);
                filteredPointsRelDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.RELATIONSHIP) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.RELATIONSHIP) ?? 0);
                filteredPointsSigDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.MEANING) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.MEANING) ?? 0);
                filteredPointsRealDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.ACCOMPLISHMENT) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.ACCOMPLISHMENT) ?? 0);
                filteredPointsVitDiff = (getMonthDimensionValue(recentInsight, 'filteredDataCombined', DimensionID.VITALITY) ?? 0) - (getMonthDimensionValue(oldestInsight, 'filteredDataCombined', DimensionID.VITALITY) ?? 0);
            }

            setPageData(prevState => ({
                ...prevState,
                ifozHistoric: generalCompanyIFOZHistoric,
                ipaHistoric: generalCompanyIAPHistoric,
                iepHistoric: generalCompanyIEPHistoric,
                ifczHistoric: generalCompanyIFCZHistoric,
                epHistoric: generalCompanyEpHistoric,
                engHistoric: generalCompanyEngHistoric,
                reaHistoric: generalCompanyReaHistoric,
                relHistoric: generalCompanyRelHistoric,
                sigHistoric: generalCompanySigHistoric,
                vitHistoric: generalCompanyVitHistoric,
                filteredIfozHistoric: filteredCompanyIFOZHistoric,
                filteredIapHistoric: companyIAPHistoric,
                filteredIepHistoric: companyIEPHistoric,
                filteredIfczHistoric: companyIFCZHistoric,
                filteredEpHistoric: companyEpHistoric,
                filteredEngHistoric: companyEngHistoric,
                filteredReaHistoric: companyReaHistoric,
                filteredRelHistoric: companyRelHistoric,
                filteredSigHistoric: companySigHistoric,
                filteredVitHistoric: companyVitHistoric,
                filteredPointEp: filteredPointsEpDiff ?? 0,
                filteredPointEng: filteredPointEngDiff ?? 0,
                filteredPointRel: filteredPointsRelDiff ?? 0,
                filteredPointSig: filteredPointsSigDiff ?? 0,
                filteredPointReal: filteredPointsRealDiff ?? 0,
                filteredPointVit: filteredPointsVitDiff ?? 0
            }));

            setAppliedRepresentationSelectedOptions([...representationFilters]);
            setAppliedGroupOptions([...selectedGroups]);

            void loadGroupParticipationReportData({
                year: yearToUse,
                month: monthToUse,
                monthsRange: monthsRangeToUse,
                representationSelectedOptions: representationFilters,
                selectedGroupOptions: selectedGroups,
                availableGroupOptions
            });
        } catch (error: any) {
            if (requestId === dataRequestIdRef.current) {
                setRepresentationSelectedOptions(appliedRepresentationSelectedOptions);
                setSelectedGroups(appliedGroupOptions);
            }
            showToast(t('error'), 'error');
        } finally {
            if (requestId !== dataRequestIdRef.current) return;
            setLoading(false);
            setLoadingIndicators(false);
            setLoadingEpHistoric(false);
            setLoadingEngHistoric(false);
            setLoadingRelHistoric(false);
            setLoadingSigHistoric(false);
            setLoadingReaHistoric(false);
            setLoadingVitHistoric(false);
        }
    }, [user, representationSelectedOptions, selectedGroupOptions, appliedRepresentationSelectedOptions, appliedGroupOptions, showToast, SignOut, clearFilteredData, range, selectGroupOptions, loadGroupParticipationReportData]);

    const loadData = useCallback(async (
        year?: string,
        month?: string,
        monthsRange?: number,
        availableGroupOptions?: InsightGroupOption[],
        requestId = ++dataRequestIdRef.current
    ) => {
        if (requestId !== dataRequestIdRef.current) return;
        setLoading(true);
        setLoadingIndicators(true);
        setLoadingEpHistoric(true);
        setLoadingEngHistoric(true);
        setLoadingRelHistoric(true);
        setLoadingSigHistoric(true);
        setLoadingReaHistoric(true);
        setLoadingVitHistoric(true);
        try {
            const company = user.company_visited?.id;
            if (!company) throw new Error('company_not_found');
            const companyCreatedAt = user.company_visited?.created_at ?? null;
            clearFilteredData();
            const yearToUse = year || new Date().getFullYear().toString();
            const monthToUse = month || (new Date().getMonth() + 1).toString();

            const monthsRangeToUse = monthsRange || 12;
            const insightsResults = await getInsightsByParameters(company, monthToUse, yearToUse, monthsRangeToUse, {
                sexes: [],
                ageRanges: [],
                groups: [],
                separateDataByEachFilter: false,
                responseScope: {
                    generalData: {
                        metrics: ['ifoz', 'iap', 'iep', 'ifcz', 'dimensions']
                    }
                }
            }, !shouldBypassStatisticsCache ? {
                enabled: true,
                userId: user.user?.id,
                scope: 'insights:general'
            } : undefined);

            if (requestId !== dataRequestIdRef.current) return;

            const companyIFOZHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'ifoz') }));
            const companyIPAHistoric = insightsResults.map((monthResult) => ({
                month: monthResult.month,
                year: monthResult.year,
                result: getMonthMetricValue(monthResult, 'generalData', 'iap')
            }));
            const companyIEPHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'iep') }));
            const companyIFCZHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year, result: getMonthMetricValue(monthResult, 'generalData', 'ifcz') }));

            const companyEPHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.POSITIVE_EMOTIONS) }));

            const companyEN = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.ENGAGEMENT) }));

            const companyRE = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.RELATIONSHIP) }));

            const companySIHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.MEANING) }));

            const companyREAHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.ACCOMPLISHMENT) }));

            const companyVITHistoric = insightsResults.map((monthResult) => ({ month: monthResult.month, year: monthResult.year,
                result: getMonthDimensionValue(monthResult, 'generalData', DimensionID.VITALITY) }));

            const recentInsight = insightsResults.find((item) => item.generalData?.dimensionsResults);
            const oldestInsight = insightsResults.slice().reverse().find((item) => item.generalData?.dimensionsResults);
            let pointsEpDiff: number = 0, pointEngDiff: number = 0, pointsRelDiff: number = 0, pointsSigDiff: number = 0, pointsRealDiff: number = 0, pointsVitDiff: number = 0;

            if (recentInsight && oldestInsight) {
                pointsEpDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.POSITIVE_EMOTIONS) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.POSITIVE_EMOTIONS) ?? 0);
                pointEngDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.ENGAGEMENT) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.ENGAGEMENT) ?? 0);
                pointsRelDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.RELATIONSHIP) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.RELATIONSHIP) ?? 0);
                pointsSigDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.MEANING) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.MEANING) ?? 0);
                pointsRealDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.ACCOMPLISHMENT) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.ACCOMPLISHMENT) ?? 0);
                pointsVitDiff = (getMonthDimensionValue(recentInsight, 'generalData', DimensionID.VITALITY) ?? 0) - (getMonthDimensionValue(oldestInsight, 'generalData', DimensionID.VITALITY) ?? 0);
            }

            setPageData(prevState => ({
                ...prevState,
                ifozHistoric: companyIFOZHistoric,
                ipaHistoric: companyIPAHistoric,
                iepHistoric: companyIEPHistoric,
                ifczHistoric: companyIFCZHistoric,
                epHistoric: companyEPHistoric,
                engHistoric: companyEN,
                reaHistoric: companyREAHistoric,
                relHistoric: companyRE,
                sigHistoric: companySIHistoric,
                vitHistoric: companyVITHistoric,
                pointEp: pointsEpDiff ?? 0,
                pointEng: pointEngDiff ?? 0,
                pointRel: pointsRelDiff ?? 0,
                pointSig: pointsSigDiff ?? 0,
                pointReal: pointsRealDiff ?? 0,
                pointVit: pointsVitDiff ?? 0
            }));

            setAppliedRepresentationSelectedOptions([]);
            setAppliedGroupOptions([]);

            void loadGroupParticipationReportData({
                year: yearToUse,
                month: monthToUse,
                monthsRange: monthsRangeToUse,
                representationSelectedOptions: [],
                selectedGroupOptions: selectedGroupOptions,
                availableGroupOptions: availableGroupOptions ?? selectGroupOptions,
                useStandardCache: !shouldBypassStatisticsCache
            });

        } catch (error) {
            if (requestId !== dataRequestIdRef.current) return;
            setRepresentationSelectedOptions(appliedRepresentationSelectedOptions);
            setSelectedGroups(appliedGroupOptions);
            showToast(t('error'), 'error');
        } finally {
            if (requestId !== dataRequestIdRef.current) return;
            setLoading(false);
            setLoadingIndicators(false);
            setLoadingEpHistoric(false);
            setLoadingEngHistoric(false);
            setLoadingRelHistoric(false);
            setLoadingSigHistoric(false);
            setLoadingReaHistoric(false);
            setLoadingVitHistoric(false);
        }
    }, [user, showToast, clearFilteredData, selectedGroupOptions, loadGroupParticipationReportData, selectGroupOptions]);

    useEffect(() => {
        if (loadingCompanies || didMountRef.current) {
            return;
        }

        const fetchData = async () => {
            didMountRef.current = true;
            try {
                const companyId = user.company_visited?.id;
                if (!companyId) return;

                const currentCompany = allCompanies.find((c: any) => c.id === companyId);

                if (currentCompany) {
                    setSurveysAllMonth(!!currentCompany.surveys_all_month);
                } else {
                    setSurveysAllMonth(true);
                }

                let currentDate = new Date();
                let year = currentDate.getFullYear();
                let month = currentDate.getMonth() + 1;

                if (currentDate.getDate() < 20) {
                    if (!currentCompany || !currentCompany.surveys_all_month) {
                        if (month === 1) {
                            month = 12;
                            year -= 1;
                        } else {
                            month -= 1;
                        }
                    }
                }

                const endDateForRange = new Date(year, month - 1, 1);
                const startDateForRange = new Date(endDateForRange);
                startDateForRange.setMonth(startDateForRange.getMonth() - 11);

                setRange({
                    startMonth: startDateForRange.getMonth() + 1,
                    startYear: startDateForRange.getFullYear(),
                    endMonth: month,
                    endYear: year
                });

                const formattedDate = `${year}-${String(month).padStart(2, '0')}`;
                setDateValue(formattedDate);

                const params: any = { company_id: companyId, groupsStatuses: [1], detailedMembersData: false };
                const userRole = Number(user.user?.role);
                if (userRole === UserRole.group_responsible) {
                    params.responsibleUsers = [user.user?.id];
                }
                const result: any = await searchGroups(params);
                const options = result.data.map((el: any) => ({ label: el.name, value: el.id }))
                    .sort((a: InsightGroupOption, b: InsightGroupOption) => a.label.localeCompare(b.label, appLocale, { sensitivity: 'base' }));

                setSelectGroupOptions(options);
                await loadData(String(year), String(month).padStart(2, '0'), 12, options);
            } catch (e) {
                console.error(e);
            }
        };

        if (user.user?.id) {
            void fetchData();
        }
    }, [user.user?.id, allCompanies, loadingCompanies, loadData, setDateValue, setSelectGroupOptions, setRange]);

    return {
        loading,
        loadingIndicators,
        loadingEpHistoric,
        loadingEngHistoric,
        loadingRelHistoric,
        loadingSigHistoric,
        loadingReaHistoric,
        loadingVitHistoric,
        loadingGroupParticipationReport,
        pageData,
        groupParticipationReportRows,
        dateValue,
        setDateValue,
        range,
        setRange,
        selectGroupOptions,
        representationSelectedOptions,
        setRepresentationSelectedOptions,
        selectedGroupOptions,
        setSelectedGroups,
        appliedRepresentationSelectedOptions,
        appliedGroupOptions,
        loadFilteredData,
        loadData,
        surveysAllMonth,
        companyCreatedAt
    };
};
